/**
 * Direct Upload Panel — push photos, point clouds, and ortho tiles from the
 * browser straight into the project's S3 folders, then register them.
 *
 *   Photos       → projects/{id}/photos | drone-photos   → POST /api/photos/register
 *   Point cloud  → .copc.laz  → projects/{id}/pointclouds → register-copc
 *                  .las/.laz  → projects/{id}/raw → POST /api/convert/jobs (Fargate → COPC)
 *                  Potree folder (metadata.json, hierarchy.bin, octree.bin)
 *                            → projects/{id}/potree-cache/{set}/ → POST /api/potree/detect
 *   Ortho tiles  → tile folder → projects/{id}/tile-cache/…     → POST /api/tiles/detect
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { api } from '../../services/api';
import { uploadBatch, formatBytes, formatRate, formatEta } from '../../services/directUpload';
import type { QueuedFile, BatchProgress, ItemState } from '../../services/directUpload';
import type { DirectUploadKind } from '../../services/api/uploadApi';
import { ConversionJobsList } from './ConversionJobsList';

type Section = 'photos' | 'pointcloud' | 'tiles';
type Phase = 'idle' | 'uploading' | 'registering' | 'done' | 'error';

interface Props {
  projectId: string;
  onUpdate: () => void;
}

const POTREE_FILES = new Set(['metadata.json', 'hierarchy.bin', 'octree.bin']);
const IMAGE_RE = /\.(jpe?g|png|webp|tiff?|heic)$/i;

// ─── Folder traversal for drag-and-drop ──────────────────────────────────────

async function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const out: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
    if (batch.length === 0) break;
    out.push(...batch);
  }
  return out;
}

async function walkEntry(entry: FileSystemEntry, prefix: string, out: QueuedFile[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
    out.push({ id: `${Date.now()}-${out.length}-${Math.random().toString(36).slice(2, 8)}`, file, relativePath: prefix + entry.name, size: file.size });
  } else if (entry.isDirectory) {
    const entries = await readAllEntries((entry as FileSystemDirectoryEntry).createReader());
    for (const e of entries) await walkEntry(e, `${prefix}${entry.name}/`, out);
  }
}

async function filesFromDrop(dt: DataTransfer): Promise<QueuedFile[]> {
  const out: QueuedFile[] = [];
  const items = Array.from(dt.items || []);
  const entries = items.map((i) => (typeof i.webkitGetAsEntry === 'function' ? i.webkitGetAsEntry() : null));
  if (entries.some(Boolean)) {
    for (const e of entries) if (e) await walkEntry(e, '', out);
    return out;
  }
  return Array.from(dt.files).map((f, i) => ({ id: `${Date.now()}-${i}`, file: f, relativePath: f.name, size: f.size }));
}

function filesFromInput(list: FileList | null): QueuedFile[] {
  if (!list) return [];
  return Array.from(list).map((f, i) => ({
    id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
    file: f,
    relativePath: ((f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name).replace(/\\/g, '/'),
    size: f.size,
  }));
}

/** Drop the top-level folder name so keys are relative to the folder's contents. */
function stripTopFolder(files: QueuedFile[]): QueuedFile[] {
  const firstSegments = new Set(files.map((f) => f.relativePath.split('/')[0]));
  const allNested = files.every((f) => f.relativePath.includes('/'));
  if (firstSegments.size === 1 && allNested) {
    return files.map((f) => ({ ...f, relativePath: f.relativePath.split('/').slice(1).join('/') }));
  }
  return files;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DirectUploadPanel({ projectId, onUpdate }: Props) {
  const [section, setSection] = useState<Section>('photos');
  const [photoKind, setPhotoKind] = useState<'photos' | 'drone-photos'>('photos');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const [summary, setSummary] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const [jobsRefresh, setJobsRefresh] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const busy = phase === 'uploading' || phase === 'registering';

  const reset = () => { setPhase('idle'); setProgress(null); setSummary(''); setError(''); };

  /** Decide which S3 kind a set of files belongs to, per section. */
  const classify = useCallback((files: QueuedFile[]): { kind: DirectUploadKind; files: QueuedFile[] } | { error: string } => {
    if (files.length === 0) return { error: 'No files selected' };
    if (section === 'photos') {
      const imgs = files.filter((f) => IMAGE_RE.test(f.relativePath));
      if (imgs.length === 0) return { error: 'No image files found (jpg, png, webp, tif, heic)' };
      // Photos are flat: keep only the file name
      return { kind: photoKind, files: imgs.map((f) => ({ ...f, relativePath: f.relativePath.split('/').pop() || f.relativePath })) };
    }
    if (section === 'pointcloud') {
      const copc = files.filter((f) => /\.copc\.laz$/i.test(f.relativePath));
      const potree = files.filter((f) => POTREE_FILES.has((f.relativePath.split('/').pop() || '').toLowerCase()));
      const rawLaz = files.filter((f) => /\.(las|laz)$/i.test(f.relativePath) && !/\.copc\.laz$/i.test(f.relativePath));
      if (copc.length) return { kind: 'copc', files: copc.map((f) => ({ ...f, relativePath: f.relativePath.split('/').pop() || f.relativePath })) };
      if (potree.length) {
        const names = new Set(potree.map((f) => (f.relativePath.split('/').pop() || '').toLowerCase()));
        const missing = [...POTREE_FILES].filter((n) => !names.has(n));
        if (missing.length) return { error: `Potree folder is missing ${missing.join(', ')}` };
        return { kind: 'potree', files: potree.map((f) => ({ ...f, relativePath: f.relativePath.split('/').pop() || f.relativePath })) };
      }
      if (rawLaz.length) return { kind: 'raw-pointcloud', files: rawLaz.map((f) => ({ ...f, relativePath: f.relativePath.split('/').pop() || f.relativePath })) };
      return { error: 'Drop a .las/.laz scan, a .copc.laz file, or a Potree octree folder' };
    }
    // tiles
    const stripped = stripTopFolder(files);
    const tiles = stripped.filter((f) => /(^|\/)\d+\/\d+\/\d+\.(png|webp|jpe?g)$/i.test(f.relativePath) || /(^|\/)(metadata|tile)\.json$/i.test(f.relativePath));
    if (tiles.length === 0) return { error: 'No tiles found. Expected a folder with {z}/{x}/{y}.png (optionally with metadata.json)' };
    return { kind: 'tiles', files: tiles };
  }, [section, photoKind]);

  const start = useCallback(async (incoming: QueuedFile[]) => {
    setError('');
    setSummary('');
    const c = classify(incoming);
    if ('error' in c) { setError(c.error); return; }

    const controller = new AbortController();
    abortRef.current = controller;
    setPhase('uploading');
    try {
      const result = await uploadBatch(c.files, {
        projectId, kind: c.kind, signal: controller.signal, onProgress: setProgress,
      });
      if (controller.signal.aborted) { setPhase('idle'); setSummary('Upload cancelled.'); return; }

      const done = result.items.filter((i) => i.status === 'done').length;
      const failed = result.items.filter((i) => i.status === 'error').length;
      const skipped = result.items.filter((i) => i.status === 'skipped');

      // Register what landed
      setPhase('registering');
      let note = '';
      if (done > 0) {
        if (c.kind === 'photos' || c.kind === 'drone-photos') {
          const r = await api.photo.registerPhotos(projectId);
          note = `${r.imported} photo${r.imported === 1 ? '' : 's'} registered (${r.gpsFound} with GPS, ${r.gpsNotFound} without)`;
        } else if (c.kind === 'copc') {
          let created = 0;
          for (const key of result.uploadedKeys) {
            const r = await api.upload.registerCopc(projectId, key);
            if (r.created) created++;
          }
          note = `${created} point cloud${created === 1 ? '' : 's'} registered`;
        } else if (c.kind === 'raw-pointcloud') {
          let queued = 0;
          const problems: string[] = [];
          for (const key of result.uploadedKeys) {
            try {
              const r = await api.convert.createJob(projectId, key);
              if (r.job.status === 'failed') problems.push(r.job.errorMessage || 'could not start');
              else queued++;
            } catch (err: any) {
              problems.push(err?.message || 'could not start');
            }
          }
          note = `${queued} scan${queued === 1 ? '' : 's'} queued for conversion to COPC`;
          if (problems.length) note += `. ${problems[0]}`;
          setJobsRefresh((n) => n + 1);
        } else if (c.kind === 'potree') {
          const r = await api.potree.detectCache(projectId);
          note = r.found ? `Point cloud detected (${r.registered || 0} new)` : 'Uploaded, but the Potree cache was not detected — check the folder contents';
        } else if (c.kind === 'tiles') {
          const r = await api.tile.detectCache(projectId);
          note = r.found ? `Tile cache detected (${r.registered || 0} new)` : 'Uploaded, but no tile cache was detected';
        }
      }
      const parts = [`${done} uploaded`];
      if (failed) parts.push(`${failed} failed`);
      if (skipped.length) parts.push(`${skipped.length} skipped`);
      setSummary(`${parts.join(', ')}${note ? `. ${note}.` : '.'}`);
      setPhase(failed && !done ? 'error' : 'done');
      onUpdate();
    } catch (err: any) {
      setPhase('error');
      setError(err?.message || 'Upload failed');
    } finally {
      abortRef.current = null;
    }
  }, [classify, projectId, onUpdate]);

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const files = await filesFromDrop(e.dataTransfer);
    start(files);
  };

  const cancel = () => { abortRef.current?.abort(); };

  const overallPct = progress && progress.totalBytes ? Math.round((progress.loadedBytes / progress.totalBytes) * 100) : 0;
  const remaining = progress ? progress.totalBytes - progress.loadedBytes : 0;

  const visibleItems = useMemo(() => {
    if (!progress) return [] as ItemState[];
    const active = progress.items.filter((i) => i.status === 'uploading' || i.status === 'error');
    const rest = progress.items.filter((i) => i.status !== 'uploading' && i.status !== 'error');
    return [...active, ...rest].slice(0, 12);
  }, [progress]);

  const sectionCopy: Record<Section, { title: string; hint: string; accept: string; folder: boolean; multiple: boolean }> = {
    photos: { title: 'Photos', hint: 'Drop JPG, PNG, WebP, or HEIC files (or a folder of them). GPS is read from EXIF automatically.', accept: 'image/*,.jpg,.jpeg,.png,.webp,.tif,.tiff,.heic', folder: true, multiple: true },
    pointcloud: { title: 'Point cloud', hint: 'Drop raw .las/.laz scans (converted to COPC in the cloud), a ready .copc.laz, or a Potree octree folder containing metadata.json, hierarchy.bin, and octree.bin.', accept: '.laz,.las,.json,.bin', folder: true, multiple: true },
    tiles: { title: 'Ortho / DSM tiles', hint: 'Drop the exported tile folder ({z}/{x}/{y}.png, with metadata.json when available).', accept: '.png,.webp,.jpg,.jpeg,.json', folder: true, multiple: true },
  };
  const copy = sectionCopy[section];

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.segment}>
          {(['photos', 'pointcloud', 'tiles'] as Section[]).map((id) => (
            <button key={id} onClick={() => { if (!busy) { setSection(id); reset(); } }} style={{ ...s.segBtn, ...(section === id ? s.segBtnActive : {}) }} disabled={busy}>
              {sectionCopy[id].title}
            </button>
          ))}
        </div>
        {section === 'photos' && (
          <div style={s.segment}>
            {([['photos', 'Facility'], ['drone-photos', 'Drone']] as Array<['photos' | 'drone-photos', string]>).map(([id, label]) => (
              <button key={id} onClick={() => !busy && setPhotoKind(id)} style={{ ...s.segBtn, ...(photoKind === id ? s.segBtnActive : {}) }} disabled={busy}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {phase === 'idle' || phase === 'done' || phase === 'error' ? (
        <div
          style={{ ...s.dropzone, ...(dragOver ? s.dropzoneActive : {}) }}
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <div style={s.dropIcon}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          </div>
          <div style={s.dropTitle}>Drop {copy.title.toLowerCase()} here</div>
          <div style={s.dropHint}>{copy.hint}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button type="button" style={s.btn} onClick={() => fileInputRef.current?.click()}>Choose files</button>
            {copy.folder && <button type="button" style={s.btn} onClick={() => folderInputRef.current?.click()}>Choose folder</button>}
          </div>
          <input ref={fileInputRef} type="file" multiple={copy.multiple} accept={copy.accept} style={{ display: 'none' }} onChange={(e) => { start(filesFromInput(e.target.files)); e.target.value = ''; }} />
          <input ref={folderInputRef} type="file" multiple style={{ display: 'none' }} {...({ webkitdirectory: '', directory: '' } as Record<string, string>)} onChange={(e) => { start(filesFromInput(e.target.files)); e.target.value = ''; }} />
        </div>
      ) : null}

      {(busy || phase === 'done' || phase === 'error') && progress && (
        <div style={s.progressBox}>
          <div style={s.progressHead}>
            <div>
              <div style={s.progressTitle}>
                {phase === 'uploading' && `Uploading ${progress.done}/${progress.items.length}`}
                {phase === 'registering' && 'Registering with the project…'}
                {phase === 'done' && 'Complete'}
                {phase === 'error' && 'Finished with errors'}
              </div>
              <div style={s.progressMeta}>
                {formatBytes(progress.loadedBytes)} of {formatBytes(progress.totalBytes)}
                {phase === 'uploading' && progress.bytesPerSecond > 0 && ` · ${formatRate(progress.bytesPerSecond)} · ${formatEta(remaining, progress.bytesPerSecond)} left`}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={s.pct}>{overallPct}%</span>
              {phase === 'uploading' && <button onClick={cancel} style={s.cancelBtn}>Cancel</button>}
              {(phase === 'done' || phase === 'error') && <button onClick={reset} style={s.btn}>Upload more</button>}
            </div>
          </div>
          <div style={s.bar}><div style={{ ...s.barFill, width: `${overallPct}%` }} /></div>

          <div style={s.items}>
            {visibleItems.map((it) => {
              const pct = it.size ? Math.round((it.loaded / it.size) * 100) : 0;
              const tone = it.status === 'done' ? '#30d158' : it.status === 'error' ? '#ff453a' : it.status === 'skipped' ? '#ffd60a' : it.status === 'cancelled' ? '#8e8e93' : '#4F7CFF';
              return (
                <div key={it.id} style={s.item}>
                  <span style={s.itemName} title={it.relativePath}>{it.relativePath}</span>
                  <span style={s.itemSize}>{formatBytes(it.size)}</span>
                  <span style={s.itemStatus}>
                    {it.status === 'uploading' && <span style={s.miniBar}><span style={{ ...s.miniFill, width: `${pct}%` }} /></span>}
                    {it.status === 'done' && <span style={{ color: tone }}>Done</span>}
                    {it.status === 'queued' && <span style={{ color: '#8e8e93' }}>Waiting</span>}
                    {it.status === 'cancelled' && <span style={{ color: tone }}>Cancelled</span>}
                    {(it.status === 'error' || it.status === 'skipped') && <span style={{ color: tone }} title={it.error}>{it.error || it.status}</span>}
                  </span>
                </div>
              );
            })}
            {progress.items.length > visibleItems.length && (
              <div style={{ ...s.item, color: '#8e8e93', fontSize: 12 }}>…and {progress.items.length - visibleItems.length} more</div>
            )}
          </div>
        </div>
      )}

      {summary && <div style={{ ...s.note, color: phase === 'error' ? '#ff453a' : '#30d158' }}>{summary}</div>}
      {error && <div style={{ ...s.note, color: '#ff453a' }}>{error}</div>}

      {section === 'pointcloud' && <ConversionJobsList projectId={projectId} refreshKey={jobsRefresh} onJobFinished={onUpdate} />}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  segment: { display: 'inline-flex', gap: 2, padding: 3, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 },
  segBtn: { border: 'none', background: 'transparent', color: '#a1a1a6', font: 'inherit', fontSize: 13, fontWeight: 500, padding: '6px 14px', borderRadius: 7, cursor: 'pointer' },
  segBtnActive: { background: 'rgba(255,255,255,0.12)', color: '#f5f5f7' },
  dropzone: { border: '1px dashed rgba(255,255,255,0.18)', borderRadius: 14, padding: '26px 20px', textAlign: 'center', background: 'rgba(255,255,255,0.02)', transition: 'background 150ms, border-color 150ms' },
  dropzoneActive: { background: 'rgba(79,124,255,0.08)', borderColor: 'rgba(79,124,255,0.6)' },
  dropIcon: { color: '#8ab0ff', marginBottom: 8, display: 'flex', justifyContent: 'center' },
  dropTitle: { color: '#f5f5f7', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' },
  dropHint: { color: '#8e8e93', fontSize: 12.5, marginTop: 4, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.45 },
  btn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', color: '#f5f5f7', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  cancelBtn: { background: 'rgba(255,69,58,0.14)', border: '1px solid rgba(255,69,58,0.35)', color: '#ff6b60', borderRadius: 9, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  progressBox: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 14 },
  progressHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 },
  progressTitle: { color: '#f5f5f7', fontSize: 14, fontWeight: 600 },
  progressMeta: { color: '#8e8e93', fontSize: 12, marginTop: 2 },
  pct: { color: '#f5f5f7', fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  bar: { height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' },
  barFill: { height: '100%', background: 'linear-gradient(90deg, #4F7CFF, #7AA6FF)', borderRadius: 3, transition: 'width 200ms ease' },
  items: { marginTop: 10, display: 'flex', flexDirection: 'column', maxHeight: 260, overflowY: 'auto' },
  item: { display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 12.5 },
  itemName: { flex: 1, minWidth: 0, color: '#d1d1d6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  itemSize: { color: '#8e8e93', fontSize: 12, flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
  itemStatus: { width: 160, flexShrink: 0, textAlign: 'right', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  miniBar: { display: 'inline-block', width: 120, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', verticalAlign: 'middle' },
  miniFill: { display: 'block', height: '100%', background: '#4F7CFF', borderRadius: 3, transition: 'width 150ms' },
  note: { fontSize: 13, lineHeight: 1.45 },
};
